/**
 * Submits the Edward Jones Quick Plan to `POST /short-form-intake` with `profile: 'edj-minimal-v1'`.
 *
 * `mapEdjFormToRequest` is the whole contract with the server:
 * - It sends no smoking answer; the server refuses one on this profile.
 * - It sends net worth as a number, not a formatted string.
 * - It always sends `skipInviteEmail: true`. EDJ restricts `invite-client`, and the server never
 *   emails a questionnaire for this profile anyway.
 * - It sends the marital answer as entered (`Married` / `Not married`). The server maps it to the
 *   models' `Divorced` at the model boundary and stores the advisor's answer unchanged.
 *
 * The server decides whether this advisor may use the profile (`resolveShortFormAccess.ts`).
 *
 * The server creates the client before it calls the models, so a response can mean "created and
 * estimated" or "created, estimate failed". Only the first carries `inferenceSetId`, and only then
 * is it reported as a created quick estimate; otherwise the advisor is pointed at "Retry Estimate".
 */
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { EDJ_QUICK_PLAN_PROFILE, getIntroIntakeFormApiEndPoint } from '@shared';
import { shortFormIntakeApi } from '@client/api/waterlily';
import { toNoonUTC } from '@/shared/lib/utils';
import type { EdjQuickPlanFormValues } from './use-edj-quick-plan-form';

export const EDJ_ESTIMATE_NOT_CALCULATED_MESSAGE =
  'Client created, but the estimate could not be calculated. Use "Retry Estimate" from the client\'s menu to try again.';

export function mapEdjFormToRequest(
  values: EdjQuickPlanFormValues,
  advisorId: string,
): ShortFormIntakeRequest {
  return {
    profile: EDJ_QUICK_PLAN_PROFILE,
    clientFirstName: values.firstName.trim(),
    clientLastName: values.lastName.trim(),
    clientBirthDate: toNoonUTC(values.birthDate),
    clientGenderAtBirth: values.sex === 'male' ? 'Male' : 'Female',
    clientMaritalStatus: values.maritalStatus,
    clientZipCode: values.zipCode,
    householdTotalAssetsValue: values.netWorth,
    intakeFormUrl: getIntroIntakeFormApiEndPoint(advisorId, false),
    skipInviteEmail: true,
  };
}

interface UseEdjShortFormIntakeOptions {
  advisorId: string;
  onSuccess?: (data: ShortFormIntakeResponse) => void;
}

export function useEdjShortFormIntake({ advisorId, onSuccess }: UseEdjShortFormIntakeOptions) {
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (values: EdjQuickPlanFormValues) =>
      shortFormIntakeApi.create(mapEdjFormToRequest(values, advisorId)),
    onSuccess: data => {
      if (data.inferenceSetId) {
        toast.success('Quick estimate created');
      } else {
        toast.warning(EDJ_ESTIMATE_NOT_CALCULATED_MESSAGE, { duration: 10_000 });
      }

      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate(`/advisors/${advisorId}/clients`);
      }
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : 'Failed to create the quick estimate. Please try again.',
      );
    },
  });

  return {
    submitEdjQuickPlan: mutation.mutate,
    isPending: mutation.isPending,
  };
}
